import { Link, useParams } from 'react-router-dom';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useScorecard } from '../../hooks/useReports';
import { ScopeRangePicker, useReportParams } from './ScopeRangePicker';
import { Card } from '../common/Card';
import { Skeleton } from '../common/Skeleton';
import { Button } from '../common/Button';
import { formatDurationSeconds, formatBudgetRemaining } from '../../utils/series';
import { useChartTheme } from '../../utils/chartTheme';
import { GradeBadge } from './GradeBadge';
import { DeltaValue } from './DeltaValue';
import { CoverageNote } from './CoverageNote';
import { EstimateBadge } from './EstimateBadge';
import { HourOfDayRow } from '../../types';

/** Same idiom as FleetReport: every metric is nullable, null renders as an em dash, never 0. */
const num = (v: number | null | undefined, digits = 0, suffix = '') =>
  v === null || v === undefined ? '—' : `${v.toFixed(digits)}${suffix}`;

function formatRange(fromIso: string, toIso: string): string {
  const from = new Date(fromIso);
  const to = new Date(toIso);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return '';
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  return `${from.toLocaleDateString(undefined, opts)} – ${to.toLocaleDateString(undefined, opts)}`;
}

/**
 * A stat tile with the same look as common/Stat, extended to carry an
 * optional DeltaValue (only the metrics with a delta field get one) and an
 * optional EstimateBadge next to the label. Stat itself takes a plain string
 * note, which can't host either component, so this stays local rather than
 * changing the shared primitive.
 */
function MetricTile({
  label, value, note, estimate, delta,
}: {
  label: string;
  value: string;
  note?: string;
  estimate?: 'mean-approx' | 'percentile-estimate';
  delta?: React.ReactNode;
}) {
  return (
    <Card className="p-4">
      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        {label}
        {estimate && <EstimateBadge kind={estimate} />}
      </p>
      <p className="text-2xl font-semibold mt-1 font-mono tabular-nums">{value}</p>
      {delta && <div className="mt-0.5">{delta}</div>}
      {note && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{note}</p>}
    </Card>
  );
}

function sortedHourOfDay(rows: HourOfDayRow[]): HourOfDayRow[] {
  return [...rows].sort((a, b) => parseInt(a.hour_of_day, 10) - parseInt(b.hour_of_day, 10));
}

function UptimeByHourChart({ rows }: { rows: HourOfDayRow[] }) {
  const t = useChartTheme();
  const data = sortedHourOfDay(rows);

  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
        No hourly data yet for this period.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={t.grid} vertical={false} />
        <XAxis dataKey="hour_of_day" tick={{ fontSize: 11, fill: t.axis }} stroke={t.grid} />
        <YAxis tick={{ fontSize: 11, fill: t.axis }} stroke={t.grid} domain={[0, 100]} unit="%" width={40} />
        <Tooltip
          contentStyle={t.tooltip}
          labelStyle={{ color: t.axis }}
          cursor={{ fill: t.grid, fillOpacity: 0.35 }}
          labelFormatter={(v: string) => `Hour ${v}:00 UTC`}
          formatter={(value: unknown) => [value === null || value === undefined ? 'no data' : `${value}%`, 'Uptime']}
        />
        <Bar dataKey="uptime" radius={[2, 2, 0, 0]} isAnimationActive={false}>
          {data.map(p => (
            <Cell
              key={p.hour_of_day}
              fill={p.uptime === null ? t.grid : p.uptime >= 99 ? t.bar : p.uptime >= 90 ? t.degraded : t.down}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function ResponseByHourChart({ rows }: { rows: HourOfDayRow[] }) {
  const t = useChartTheme();
  const data = sortedHourOfDay(rows);

  if (data.every(p => p.mean_response === null)) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
        No response times recorded for this period.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={t.grid} vertical={false} />
        <XAxis dataKey="hour_of_day" tick={{ fontSize: 11, fill: t.axis }} stroke={t.grid} />
        <YAxis tick={{ fontSize: 11, fill: t.axis }} stroke={t.grid} unit="ms" width={56} />
        <Tooltip
          contentStyle={t.tooltip}
          labelStyle={{ color: t.axis }}
          cursor={{ stroke: t.grid }}
          labelFormatter={(v: string) => `Hour ${v}:00 UTC`}
          formatter={(value: unknown) => [value === null || value === undefined ? 'no data' : `${value} ms`, 'Mean response']}
        />
        <Line
          type="monotone"
          dataKey="mean_response"
          stroke={t.line}
          strokeWidth={2}
          dot={false}
          connectNulls={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function ServiceScorecard() {
  const { id } = useParams<{ id: string }>();
  const serviceId = parseInt(id!, 10);
  const validId = Number.isFinite(serviceId);

  const { query } = useReportParams();
  // The scorecard endpoint takes only from/to/compare -- scope doesn't apply
  // to a single service, so it's deliberately left out of the request.
  const params = { from: query.from, to: query.to, ...(query.compare ? { compare: query.compare } : {}) };
  const { data, isLoading, isError } = useScorecard(serviceId, params);

  if (!validId) {
    return (
      <Card className="p-10 text-center text-gray-500 dark:text-gray-400">
        <p>That isn't a valid service.</p>
        <Link to="/reports" className="no-print text-brand-600 dark:text-brand-400 hover:underline mt-2 inline-block">
          Back to reports
        </Link>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Loading scorecard">
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    );
  }
  if (isError || !data) {
    return <Card className="p-10 text-center text-gray-500 dark:text-gray-400">Could not load this report.</Card>;
  }

  const { current, previous, hour_of_day, meta } = data;
  const meanApprox = meta.source.response_mean === 'hourly-approx';
  const p95Estimated = meta.source.percentiles === 'hourly-estimate';
  const showDeltas = previous !== null;

  const remainingSeconds = current.error_budget_remaining_minutes === null
    ? null
    : current.error_budget_remaining_minutes * 60;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link
            to={`/services/${serviceId}`}
            className="no-print text-sm text-brand-600 dark:text-brand-400 hover:underline"
          >
            ← Back to service
          </Link>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <h1 className="text-2xl font-bold tracking-tight">{current.service_name}</h1>
            <GradeBadge grade={current.grade} budgetConsumed={current.error_budget_consumed} />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            SLA target {current.sla_target}%{current.sla_target_inherited ? ' (using the default — no target set on this service)' : ''}
            {' · '}
            {formatRange(meta.range.from, meta.range.to)}
          </p>
          <CoverageNote coverage={meta.coverage} />
        </div>
        <Button variant="secondary" size="sm" className="no-print" onClick={() => window.print()}>
          Print
        </Button>
      </div>

      <div className="no-print">
        <ScopeRangePicker hideScope />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        <MetricTile
          label="Uptime"
          value={num(current.uptime, 3, '%')}
          note={`${current.hours_present}/${current.hours_expected} hours of data`}
          delta={showDeltas && <DeltaValue value={current.uptime_delta} unit="pp" />}
        />
        <MetricTile
          label="MTTR"
          value={formatDurationSeconds(current.mttr_seconds)}
          note="mean time to resolve"
          delta={showDeltas && <DeltaValue value={current.mttr_delta} unit="s" invert decimals={0} />}
        />
        <MetricTile
          label="Incidents"
          value={String(current.incident_count)}
          note={current.ongoing_count > 0 ? `${current.ongoing_count} ongoing` : 'none ongoing'}
          delta={showDeltas && <DeltaValue value={current.incident_delta} invert decimals={0} />}
        />
        <MetricTile
          label="Longest outage"
          value={formatDurationSeconds(current.longest_seconds)}
          note="longest single incident"
        />
        <MetricTile
          label="Mean response"
          value={num(current.mean_response, 0, ' ms')}
          estimate={meanApprox ? 'mean-approx' : undefined}
          note="average response time"
        />
        <MetricTile
          label="p95"
          value={num(current.p95, 0, ' ms')}
          estimate={p95Estimated ? 'percentile-estimate' : undefined}
          note="95th percentile response time"
        />
        <MetricTile
          label="Total downtime"
          value={formatDurationSeconds(current.downtime_seconds)}
          note="total time down in period"
        />
        <MetricTile
          label="Error budget remaining"
          value={formatBudgetRemaining(remainingSeconds)}
          note={current.error_budget_consumed === null ? undefined : `${current.error_budget_consumed}% consumed`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <h2 className="text-sm font-semibold mb-3">Uptime by hour of day</h2>
          <UptimeByHourChart rows={hour_of_day} />
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">Hour of day (UTC)</p>
        </Card>
        <Card className="p-4">
          <h2 className="text-sm font-semibold mb-3">Mean response by hour of day</h2>
          <ResponseByHourChart rows={hour_of_day} />
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">Hour of day (UTC)</p>
        </Card>
      </div>
    </div>
  );
}
